import Box from '@mui/material/Box'
import { resetCache as resetCsvConfigCache } from 'api/csv'
import * as configApi from 'api/ocrBuildersConfig'
import { testConnection as testConnectionApi } from 'api/ocrBuildersConfig'
import DialogBase from 'components/dialog/Base'
import { useRootLoaderData } from 'components/Root.loader'
import { useAlert } from 'hooks/useDialog'
import { useState, useEffect, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useRevalidator } from 'react-router-dom'
import { OcrBuilder, CreateOcrBuilder, UpdateOcrBuilder, OcrTest, TestResult } from 'types'
import { checkFilesErrors } from 'utils/file'

import Step1 from './Step1'
import Step2 from './Step2'

const NEW_ENTRY_ID = 0
const DEFAULT_FORM_VALUES = { subGroup: '', documentTypeName: '', invokeUri: '', secretKey: '' }

type Param = {
  ocrBuilderConfigID: number // 0: new, -1: close
  setOcrBuilderConfigID: (builderId: number) => void // to close the dialog
  ocrBuilderData?: OcrBuilder
}
const AddOcrBuilderDialog = ({
  ocrBuilderConfigID,
  setOcrBuilderConfigID,
  ocrBuilderData,
}: Param) => {
  const revalidator = useRevalidator()
  const [activeStep, setActiveStep] = useState(0)
  const conTestInputRef = useRef<OcrTest | null>(null)
  const conTestInput = conTestInputRef.current

  const open = ocrBuilderConfigID >= NEW_ENTRY_ID
  const rootData = useRootLoaderData()
  const vSubGroupNames = rootData.verifyGroups
  const alert = useAlert()

  const [loading, setLoading] = useState(false)
  const [testResult, setTestResult] = useState<TestResult | null>(null)
  const [file, setFile] = useState<(File & { id: number }) | null>(null)

  const form1 = useForm<OcrTest>({
    defaultValues: DEFAULT_FORM_VALUES,
  })
  const form2 = useForm<{ mappings: CreateOcrBuilder['mappings'] }>()

  const handleFilesAdded = async (files: (File & { id: number })[]) => {
    // TODO: check filetype and size
    if (!files || files.length === 0) {
      return
    }

    const f = files.pop()
    f!.id = 1
    setFile(f!)
  }

  const handleFileDeleted = () => {
    setFile(null)
  }

  const fileErrors = checkFilesErrors(file ? [file] : [], true)

  useEffect(() => {
    if (!open) return

    if (ocrBuilderConfigID === NEW_ENTRY_ID) {
      form1.reset(DEFAULT_FORM_VALUES)
    } else {
      form1.reset(ocrBuilderData)
    }

    setFile(null)
    setActiveStep(0)
  }, [open, form1, ocrBuilderData, ocrBuilderConfigID])

  const close = () => {
    setOcrBuilderConfigID(-1)
  }

  const submitCreateOrUpdate = form2.handleSubmit(async ({ mappings }) => {
    if (mappings.every((m) => m.display === false)) {
      return await alert({ message: '最低一つは表示設定する必要があります' })
    }
    setLoading(true)
    const ocrDataCreate: CreateOcrBuilder = {
      subGroup: conTestInput!.subGroup,
      documentTypeName: conTestInput!.documentTypeName,
      no: testResult!.no,
      templateID: testResult!.templateID,
      templateName: testResult!.templateName,
      mappings: mappings,
    }
    const ocrDataUpdate: UpdateOcrBuilder = {
      documentTypeName: conTestInput!.documentTypeName,
      secretKey: conTestInput!.secretKey,
      invokeUri: conTestInput!.invokeUri,
      subGroup: conTestInput!.subGroup,
      no: testResult!.no,
      templateID: testResult!.templateID,
      templateName: testResult!.templateName,
      mappings: mappings,
    }

    resetCsvConfigCache()
    try {
      ocrBuilderConfigID === NEW_ENTRY_ID
        ? await configApi.createOcrBuilderConfig(ocrDataCreate)
        : await configApi.updateOcrBuilderConfig(ocrBuilderConfigID, ocrDataUpdate)
      revalidator.revalidate()
      close()
      alert({ message: '登録しました' })
    } catch (err: any) {
      const message = err.response?.data?.message || '保存に失敗しました'
      alert({ message })
    }
    setLoading(false)
  })

  const submitSave = form1.handleSubmit(async (data) => {
    // TODO: name, key only
    setLoading(true)
    try {
      if (ocrBuilderConfigID < 0) return
      await configApi.updateOcrBuilderConfig(ocrBuilderConfigID, data)
      revalidator.revalidate()
      close()
      alert({ message: '登録しました' })
    } catch (err: any) {
      const message = err.response?.data?.message || '保存に失敗しました'
      alert({ message })
    }
    setLoading(false)
  })

  const submitTest = form1.handleSubmit(async (data) => {
    conTestInputRef.current = data

    if (!file) {
      return alert({ message: 'ファイルをアップロードしてください' })
    }
    if (fileErrors.length > 0) {
      return alert({ message: 'ファイルをご確認ください' })
    }
    conTestInputRef.current.file = file

    setLoading(true)
    try {
      const testResult = await testConnectionApi(conTestInputRef.current)
      setTestResult(testResult)
      form2.reset(testResult)
      setActiveStep(1)
    } catch (err: any) {
      const message = err.response?.data?.message || 'テストに失敗しました'
      alert({ message })
    }
    setLoading(false)
  })

  const doTest = () => {
    submitTest()
  }

  const doCreateOrUpdate = () => {
    if (conTestInput) {
      submitCreateOrUpdate()
    }
  }
  const doSave = () => {
    submitSave()
  }

  useEffect(() => {
    if (ocrBuilderConfigID > 0 && !ocrBuilderData) {
      // ありえないはず
      alert({ message: 'system error' })
    }
  }, [ocrBuilderConfigID, ocrBuilderData, alert])

  if (ocrBuilderConfigID > 0 && !ocrBuilderData) {
    return <></>
  }

  return (
    <DialogBase
      open={open}
      loading={loading}
      maxWidth="md"
      align="left"
      title={`Template OCR連携設定 (Step ${activeStep + 1}/2)`}
      onCancel={close}
      onClose={close}
      onAction={activeStep === 0 ? doTest : doCreateOrUpdate}
      actionText={activeStep === 0 ? '接続テスト' : '保存'}
      cancelText="キャンセル"
      extraActionText="保存"
      onExtraAction={activeStep === 0 && ocrBuilderConfigID > 0 ? doSave : undefined}
    >
      <Box sx={{ minHeight: 380 }}>
        {activeStep === 0 ? (
          <FormProvider {...form1}>
            <Step1
              handleFilesAdded={handleFilesAdded}
              handleFileDeleted={handleFileDeleted}
              fileData={file}
              ocrBuilderData={ocrBuilderData}
              errors={fileErrors}
            />
          </FormProvider>
        ) : (
          <FormProvider {...form2}>
            {testResult && (
              <Step2
                testResult={testResult}
                file={file!}
                isVerify={vSubGroupNames.includes(conTestInputRef.current!.subGroup)}
              />
            )}
          </FormProvider>
        )}
      </Box>
    </DialogBase>
  )
}

export default AddOcrBuilderDialog
